// Notes peek for the Deal / Lead pipelines: a count button on every card/row and one right-side panel.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { StickyNote } from "lucide-react";
import api from "../../lib/api";
import NotesPeekPanel from "./NotesPeekPanel";

const Ctx = createContext(null);

export function NotesPeekProvider({ children, onOpen360 }) {
  const [counts, setCounts] = useState({});
  const [peek, setPeek] = useState(null);
  const queue = useRef(new Set());
  const asked = useRef(new Set());
  const timer = useRef(null);
  const flush = useCallback(async () => {
    const ids = [...queue.current]; queue.current.clear();
    for (let i = 0; i < ids.length; i += 500) {
      try {
        const { data } = await api.post("/crm/persons/notes-count", { person_ids: ids.slice(i, i + 500) });
        setCounts((c) => ({ ...c, ...(data.counts || {}) }));
      } catch { /* counts are a hint only */ }
    }
  }, []);
  const want = useCallback((id) => {
    if (!id || asked.current.has(id)) return;
    asked.current.add(id); queue.current.add(id);
    clearTimeout(timer.current); timer.current = setTimeout(flush, 150);
  }, [flush]);
  const setCount = useCallback((id, n) => setCounts((c) => ({ ...c, [id]: n })), []);
  return (
    <Ctx.Provider value={{ counts, want, open: setPeek, active: peek?.id }}>
      {children}
      <NotesPeekPanel peek={peek} onClose={() => setPeek(null)} onCount={setCount} onOpen360={onOpen360} />
    </Ctx.Provider>
  );
}

export function NotesButton({ personId, name, compact = false }) {
  const ctx = useContext(Ctx);
  useEffect(() => { ctx?.want(personId); }, [ctx, personId]);
  if (!ctx || !personId) return null;
  const n = ctx.counts[personId];
  const on = ctx.active === personId;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); ctx.open({ id: personId, name }); }}
      title="Peek notes" data-testid={`notes-peek-btn-${personId}`}
      className={`shrink-0 inline-flex items-center gap-1 rounded border font-mono transition-colors ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[10px] uppercase tracking-wider"} ${on ? "border-yellow-400/60 bg-yellow-500/20 text-yellow-100" : n ? "border-yellow-500/30 bg-yellow-500/10 text-yellow-200 hover:bg-yellow-500/20" : "border-white/10 bg-white/[0.03] text-slate-500 hover:text-slate-300"}`}>
      <StickyNote className="w-3 h-3" />{!compact && "Notes"}
      <span className="tabular-nums" data-testid={`notes-peek-count-${personId}`}>{n ?? "·"}</span>
    </button>
  );
}
